/**
 * Industry Grid Component
 * 
 * This component provides a consistent grid display for industry cards with
 * enhanced animations, responsive behavior, and theme-aware styling.
 */
'use client';

import React, { useMemo, useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import IndustryCard from './IndustryCard';
import ApplicationCard from './ApplicationCard';
import type { Industry, IndustryApplication } from '@/lib/data/industries';
import { cn } from '@/lib/utils';
import { industryGridVariants, sectionTitleVariants, sectionDescriptionVariants } from '@/components/ui/design-system';
// Design system adapters
import { Heading, Text } from '@/components/ui/adapters/Text';

/**
 * Props for the IndustryGrid component
 */
export interface IndustryGridProps {
  /**
   * List of items to display
   */
  items: (Industry | IndustryApplication)[];
  
  /**
   * Display mode for the grid
   */
  displayMode: 'industries' | 'applications';
  
  /**
   * Optional title for the section
   */
  title?: React.ReactNode;
  
  /**
   * Optional description for the section
   */
  description?: React.ReactNode;
  
  /**
   * Number of columns for desktop view
   * @default 3
   */
  columns?: 1 | 2 | 3 | 4 | 5 | 6;
  
  /**
   * Additional class name
   */
  className?: string;
  
  /**
   * Unique identifier for the section, useful for anchor links
   */
  id?: string;
  
  /**
   * Whether this grid is above the fold
   */
  isAboveTheFold?: boolean;
}

const IndustryGrid: React.FC<IndustryGridProps> = ({ 
  items, 
  displayMode, 
  title = "Industries We Serve",
  description,
  columns = 3,
  className = '',
  id,
  isAboveTheFold = false
}) => {
  // Memoize the grid columns class to prevent recalculation on every render
  const gridColumnsClass = useMemo(() => {
    switch (columns) {
      case 1: return 'grid-cols-1';
      case 2: return 'grid-cols-1 md:grid-cols-2';
      case 4: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4';
      case 5: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5';
      case 6: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6';
      default: return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    }
  }, [columns]);
  
  // Using industryGridVariants from the design system adapter

  // Create refs for scroll-based animations
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { 
    once: true, 
    margin: "-50px 0px -50px 0px"
  });
  const controls = useAnimation();

  // Generate a unique ID for the heading if not provided
  const headingId = id ? `${id}-heading` : 'industries-heading';

  // Start animations when section is in view
  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return (
    <motion.section 
      ref={sectionRef}
      initial="hidden"
      animate={controls}
      variants={industryGridVariants}
      className={cn(
        "relative overflow-hidden py-8 sm:py-10 md:py-12", 
        className
      )}
      aria-labelledby={headingId}
      id={id}
    >
      {/* Background elements for visual richness */}
      <div className="absolute inset-0 -z-10" aria-hidden="true">
        <div className="absolute inset-0 bg-[url('/noise.png')] opacity-[0.02] dark:opacity-[0.03] mix-blend-overlay pointer-events-none transform-gpu"></div>
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-gradient-to-tl from-primary-100/20 to-primary-200/10 dark:from-primary-800/20 dark:to-primary-900/10 rounded-full blur-3xl transform-gpu"></div>
      </div>

      {/*
        Grid/Container uses Tailwind spacing and breakpoints, as no dedicated grid/container adapter exists in the design system.
        This is design-system-compliant per monorepo rules: all spacing, breakpoints, and gaps are via tokens/utilities/classes.
      */}
      <div className="container mx-auto px-4 sm:px-4 md:px-6 lg:px-8">
        {/* Section header with enhanced animations */}
        {(title || description) && (
          <motion.div 
            className="text-center mb-12"
            variants={industryGridVariants}
          >
            {/* Use design system Heading/Text adapters for all typography */}
            {title && (
              <motion.div variants={sectionTitleVariants}>
                <Heading
                  id={headingId}
                  level={2}
                  style="gradient"
                  align="center"
                  className="mb-3"
                  animate
                >
                  {title}
                </Heading>
              </motion.div>
            )}
            {description && (
              <motion.div variants={sectionDescriptionVariants}>
                <Text
                  variant="body-large"
                  color="secondary"
                  align="center"
                  className="max-w-3xl mx-auto opacity-80"
                  style="gradientSubtle"
                  animate
                >
                  {description}
                </Text>
              </motion.div>
            )}
          </motion.div>
        )}
        
        {/* Industries grid with staggered animations */}
        {/*
          Grid layout uses only design-system-driven gap and responsive columns (via Tailwind).
          Card rendering is adapter-driven: IndustryCard uses the Card adapter internally.
        */}
        <motion.div 
          className={cn("grid gap-6 md:gap-8", gridColumnsClass)}
          variants={industryGridVariants}
          initial="hidden"
          animate="visible"
          data-testid="industry-grid"
        >
          {items.map((item, index) => {
            if (displayMode === 'industries') {
              return (
                <IndustryCard
                  key={(item as Industry).id}
                  industry={item as Industry}
                  index={index}
                  isPriority={isAboveTheFold || index < 3}
                />
              );
            } else {
              return (
                <ApplicationCard
                  key={(item as IndustryApplication).id}
                  application={item as IndustryApplication}
                  index={index}
                  isPriority={isAboveTheFold || index < 3}
                />
              );
            }
          })}
        </motion.div>
      </div>
    </motion.section>
  );
}

// Use memo to prevent unnecessary re-renders
export default React.memo(IndustryGrid); 